// Composer Studio — 캔버스 우클릭 컨텍스트 메뉴 셸 (양식 탭 노드 · 작성 탭 위젯 공용, 2026-07-15)
//
// body portal + fixed 좌표로 띄운다 — 캔버스 줌/팬 transform이 fixed의 containing block을
// 가로채므로 캔버스 안에 두면 좌표가 틀어진다(차단 툴팁 ftBlockedHint와 동일 이유).
// 항목은 데이터(액션/구분선/서브메뉴)로 받아 렌더만 담당하고, 화면 경계에서 플립·클램프한다.
// 탭별 항목 구성(무엇을 추가/복사/삭제하는가)은 호출부(EditorPage)가 조립한다.

import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';

export type ComposerContextMenuItem =
  | {
      type: 'action';
      label: string;
      /** 우측 회색 단축키 힌트(예: 'Ctrl+C'). */
      shortcut?: string;
      disabled?: boolean;
      /** disabled 사유 — hover title로 노출. */
      disabledReason?: string;
      /** 위험 동작(삭제 등) — 빨간 텍스트. */
      danger?: boolean;
      onSelect: () => void;
    }
  | { type: 'separator' }
  /**
   * 3단 중첩(서브메뉴 안 서브메뉴/검색목록) 가능 — 단 중첩 자식을 가진 서브메뉴는 패널이 스크롤
   * 컨테이너가 되지 않으므로(그래야 손자 패널이 안 잘림, 2026-07-22) **항목 수를 적게 유지**할 것.
   * 긴 목록은 손자 쪽(`search-list`)에 두거나 스크롤 없는 높이로 설계한다.
   */
  | { type: 'submenu'; label: string; items: ComposerContextMenuItem[] }
  | {
      /**
       * 검색 목록 서브메뉴 — 항목이 많은 목록(미배치 위젯 풀 등)용. 상단 검색 입력 + 아래
       * 전체 목록(스크롤 직접 선택 가능), 입력 시 label/hint 부분일치 필터.
       */
      type: 'search-list';
      label: string;
      placeholder?: string;
      disabled?: boolean;
      disabledReason?: string;
      options: {
        id: string;
        label: string;
        hint?: string;
        onSelect: () => void;
        /** 항목 hover 통지(enter=행 rect / leave=null) — 미배치 위젯 미니 미리보기 등 외부 표면 구동. */
        onHover?: (rect: DOMRect | null) => void;
      }[];
      /** 서브메뉴 닫힘/언마운트 시 hover 잔존 정리(다른 서브메뉴로 전환 등 mouseleave 미발화 경로). */
      onHoverClear?: () => void;
    };

/** 메뉴 패널 공통 룩 — 메인/서브메뉴 동일. */
const PANEL_CLASS =
  'min-w-[180px] rounded-md border border-gray-300 bg-white py-1 text-sm text-gray-800 shadow-lg';

/**
 * 서브메뉴 패널 세로 뷰포트 보정 — 패널이 부모 항목 top 기준(absolute top-0)이라 목록이 길거나
 * 메뉴가 화면 끝에 붙으면 상/하단이 잘린다. 열릴 때 실측해 화면 안으로 translateY 보정.
 */
function useSubmenuViewportShift(
  open: boolean,
  /**
   * 패널 높이를 바꾸는 값(검색 결과 개수 등) — 바뀌면 재실측한다. 열림 시점 1회만 재면, 검색으로
   * 목록이 짧아졌을 때 옛 보정값이 남아 패널이 안쪽으로 밀린 채 유지된다(2026-07-22 리뷰 지적).
   */
  contentKey: unknown = null,
): {
  panelRef: RefObject<HTMLDivElement>;
  shiftY: number;
} {
  const panelRef = useRef<HTMLDivElement>(null);
  const [shiftY, setShiftY] = useState(0);
  // 재실측 시 현재 적용 중인 보정을 빼기 위한 최신값(effect deps에는 넣지 않는다 — 넣으면 루프).
  const shiftYRef = useRef(0);
  shiftYRef.current = shiftY;
  useLayoutEffect(() => {
    if (!open) {
      setShiftY(0);
      return;
    }
    const el = panelRef.current;
    if (!el) return;
    // 이전 보정이 적용된 상태에서 재는 것을 피하려고 원위치 기준으로 환산(dy 누적 방지).
    const rect = el.getBoundingClientRect();
    const top = rect.top - shiftYRef.current;
    const bottom = rect.bottom - shiftYRef.current;
    let dy = 0;
    if (bottom > window.innerHeight - 4) dy = window.innerHeight - 4 - bottom;
    if (top + dy < 4) dy = 4 - top;
    setShiftY(dy);
  }, [open, contentKey]);
  return { panelRef, shiftY };
}

function MenuList({
  items,
  onDone,
}: {
  items: ComposerContextMenuItem[];
  /** 항목 실행/외부 닫기 공용 — 메뉴 전체를 닫는다. */
  onDone: () => void;
}) {
  // 열린 서브메뉴 인덱스 — hover로 전환(한 번에 하나만).
  const [openSub, setOpenSub] = useState<number | null>(null);
  return (
    <div role="menu" className={PANEL_CLASS}>
      {items.map((item, i) => {
        if (item.type === 'separator')
          return <div key={i} role="separator" className="my-1 border-t border-gray-200" />;
        if (item.type === 'submenu')
          return (
            <SubMenuItem
              key={i}
              item={item}
              open={openSub === i}
              onOpen={() => setOpenSub(i)}
              onDone={onDone}
            />
          );
        if (item.type === 'search-list')
          return (
            <SearchListSubMenu
              key={i}
              item={item}
              open={openSub === i}
              onOpen={() => setOpenSub(i)}
              onDone={onDone}
            />
          );
        return (
          <button
            key={i}
            type="button"
            role="menuitem"
            disabled={item.disabled}
            title={item.disabled ? item.disabledReason : undefined}
            className={`flex w-full items-center justify-between gap-6 px-3 py-1.5 text-left ${
              item.disabled
                ? 'cursor-default text-gray-300'
                : item.danger
                  ? 'text-red-600 hover:bg-red-50'
                  : 'hover:bg-gray-100'
            }`}
            onMouseEnter={() => setOpenSub(null)}
            onClick={() => {
              if (item.disabled) return;
              // 닫은 뒤 실행 — 액션이 모달/토스트를 열어도 메뉴가 위에 남지 않게.
              onDone();
              item.onSelect();
            }}
          >
            <span>{item.label}</span>
            {item.shortcut ? <span className="text-xs text-gray-400">{item.shortcut}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

function SubMenuItem({
  item,
  open,
  onOpen,
  onDone,
}: {
  item: Extract<ComposerContextMenuItem, { type: 'submenu' }>;
  open: boolean;
  onOpen: () => void;
  onDone: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [side, setSide] = useState<'right' | 'left'>('right');
  const { panelRef, shiftY } = useSubmenuViewportShift(open);
  // 손자 패널(3단)을 가진 서브메뉴인지 — 스크롤 컨테이너화 여부를 가른다(아래 패널 주석 참조).
  const hasNestedSub = item.items.some(
    (it) => it.type === 'submenu' || it.type === 'search-list',
  );
  // 열릴 때 우측 여유 측정 — 부족하면 좌측으로 플립(서브메뉴 최소폭 기준 근사).
  useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setSide(rect.right + 200 > window.innerWidth ? 'left' : 'right');
  }, [open]);
  return (
    <div ref={ref} className="relative" onMouseEnter={onOpen}>
      <button
        type="button"
        role="menuitem"
        aria-haspopup="menu"
        aria-expanded={open}
        className={`flex w-full items-center justify-between gap-6 px-3 py-1.5 text-left ${
          open ? 'bg-gray-100' : 'hover:bg-gray-100'
        }`}
        onClick={onOpen}
      >
        <span>{item.label}</span>
        <span className="text-xs text-gray-400">▸</span>
      </button>
      {open ? (
        // 항목이 많은 서브메뉴(팔레트 전체 등)는 세로 스크롤로 수용 + 뷰포트 세로 보정.
        // ⚠️ 단 **중첩 자식(3단)이 있으면 스크롤을 걸지 않는다** — overflow 컨테이너가 되면 그 안에서
        // 열리는 손자 패널이 잘려 어긋난다(2026-07-22 제보: '이동 › 다른 섹션으로'). 중첩을 가진
        // 서브메뉴는 항목 수를 적게 유지하고, 세로 넘침은 useSubmenuViewportShift 보정에 맡긴다.
        <div
          ref={panelRef}
          className={`absolute top-0 z-10 ${hasNestedSub ? '' : 'max-h-[60vh] overflow-y-auto'} ${
            side === 'right' ? 'left-full' : 'right-full'
          }`}
          style={shiftY ? { transform: `translateY(${shiftY}px)` } : undefined}
        >
          <MenuList items={item.items} onDone={onDone} />
        </div>
      ) : null}
    </div>
  );
}

function SearchListSubMenu({
  item,
  open,
  onOpen,
  onDone,
}: {
  item: Extract<ComposerContextMenuItem, { type: 'search-list' }>;
  open: boolean;
  onOpen: () => void;
  onDone: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [side, setSide] = useState<'right' | 'left'>('right');
  const [query, setQuery] = useState('');
  // 검색어로 목록 높이가 바뀌면 세로 보정을 재실측(query를 contentKey로).
  const { panelRef, shiftY } = useSubmenuViewportShift(open, query);
  useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setSide(rect.right + 290 > window.innerWidth ? 'left' : 'right');
  }, [open]);
  // 닫히면 검색 초기화 — 재열림 시 전체 목록부터(이전 필터 잔존 방지).
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);
  // 닫힘/언마운트 시 hover 잔존 정리 — 다른 서브메뉴 전환·메뉴 닫힘은 옵션 mouseleave가
  // 발화하지 않는 경로라 외부 표면(미니 미리보기)이 남을 수 있음.
  const hoverClearRef = useRef(item.onHoverClear);
  hoverClearRef.current = item.onHoverClear;
  useEffect(() => {
    if (!open) hoverClearRef.current?.();
    return () => hoverClearRef.current?.();
  }, [open]);
  if (item.disabled) {
    return (
      <button
        type="button"
        role="menuitem"
        disabled
        title={item.disabledReason}
        className="flex w-full cursor-default items-center justify-between gap-6 px-3 py-1.5 text-left text-gray-300"
      >
        <span>{item.label}</span>
        <span className="text-xs text-gray-300">▸</span>
      </button>
    );
  }
  const q = query.trim().toLowerCase();
  // 미입력 = 전체 목록(스크롤 직접 선택), 입력 = label/hint 부분일치 필터.
  const filtered = q
    ? item.options.filter(
        (o) =>
          o.label.toLowerCase().includes(q) || (o.hint ?? '').toLowerCase().includes(q),
      )
    : item.options;
  const pick = (o: { onSelect: () => void }) => {
    onDone(); // 닫은 뒤 실행(액션 항목과 동일 규약)
    o.onSelect();
  };
  return (
    <div ref={ref} className="relative" onMouseEnter={onOpen}>
      <button
        type="button"
        role="menuitem"
        aria-haspopup="menu"
        aria-expanded={open}
        className={`flex w-full items-center justify-between gap-6 px-3 py-1.5 text-left ${
          open ? 'bg-gray-100' : 'hover:bg-gray-100'
        }`}
        onClick={onOpen}
      >
        <span>{item.label}</span>
        <span className="text-xs text-gray-400">▸</span>
      </button>
      {open ? (
        <div
          ref={panelRef}
          className={`absolute top-0 z-10 ${side === 'right' ? 'left-full' : 'right-full'}`}
          style={shiftY ? { transform: `translateY(${shiftY}px)` } : undefined}
        >
          <div className={`${PANEL_CLASS} w-[280px]`}>
            <div className="px-2 pb-1 pt-0.5">
              <input
                type="text"
                autoFocus
                value={query}
                placeholder={item.placeholder ?? '검색'}
                className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-brand-400"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  // 캔버스/에디터 전역 단축키로 새지 않게 차단. Enter = 첫 매칭 선택(키보드 흐름).
                  e.stopPropagation();
                  if (e.key === 'Enter' && filtered.length > 0) pick(filtered[0]);
                }}
              />
            </div>
            <div className="max-h-[50vh] overflow-y-auto" role="menu">
              {filtered.length === 0 ? (
                <p className="px-3 py-2 text-xs text-gray-400">일치하는 항목이 없습니다.</p>
              ) : (
                filtered.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    role="menuitem"
                    className="flex w-full flex-col items-start px-3 py-1.5 text-left hover:bg-gray-100"
                    onClick={() => pick(o)}
                    // hover 통지 — 미배치 위젯 미니 미리보기 구동(풀 행과 동일 UX, 2026-07-16).
                    onMouseEnter={
                      o.onHover
                        ? (e) => o.onHover?.(e.currentTarget.getBoundingClientRect())
                        : undefined
                    }
                    onMouseLeave={o.onHover ? () => o.onHover?.(null) : undefined}
                  >
                    <span className="w-full truncate">{o.label}</span>
                    {o.hint ? (
                      <span className="w-full truncate text-[10px] text-gray-400">{o.hint}</span>
                    ) : null}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function ComposerContextMenu({
  x,
  y,
  items,
  onClose,
}: {
  /** 커서 위치(clientX/Y) — 화면 경계 초과 시 반대편 플립·클램프. */
  x: number;
  y: number;
  items: ComposerContextMenuItem[];
  onClose: () => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x, y });
  // 마운트 직후 실측 클램프 — 우/하단이 넘치면 커서 반대편으로.
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let nx = x;
    let ny = y;
    if (x + rect.width > window.innerWidth - 4) nx = Math.max(4, x - rect.width);
    if (y + rect.height > window.innerHeight - 4) ny = Math.max(4, y - rect.height);
    setPos({ x: nx, y: ny });
  }, [x, y]);
  // 닫기 — 외부 pointerdown(캡처)/Esc(캡처: 에디터 Esc 드릴업으로 새지 않게)/메뉴 밖 휠/리사이즈/창 블러.
  useEffect(() => {
    const onPointerDown = (ev: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(ev.target as Node)) onClose();
    };
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      ev.stopPropagation();
      onClose();
    };
    const onWheel = (ev: Event) => {
      if (rootRef.current && !rootRef.current.contains(ev.target as Node)) onClose();
    };
    const onDismiss = () => onClose();
    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('wheel', onWheel, true);
    window.addEventListener('resize', onDismiss);
    window.addEventListener('blur', onDismiss);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('wheel', onWheel, true);
      window.removeEventListener('resize', onDismiss);
      window.removeEventListener('blur', onDismiss);
    };
  }, [onClose]);
  return createPortal(
    <div
      ref={rootRef}
      className="fixed z-[80]"
      style={{ left: pos.x, top: pos.y }}
      // 메뉴 위 재우클릭은 브라우저 기본 메뉴 억제(그대로 유지).
      onContextMenu={(e) => e.preventDefault()}
    >
      <MenuList items={items} onDone={onClose} />
    </div>,
    document.body,
  );
}
